import React from "react";
import { useNotificationContext } from "@limitless/common/hooks";
import { useExpertProfileContext } from "../expert-profile/ExpertProfileContext";
import { signOutUser } from "../../apis/authentication-api/AuthenticationAPI";
const INITIAL_AUTH_STATE = {
  authenticated: false,
  errorMessage: "",
  webSocketSubscribed: false,
  graphClientReady: false
};
const SIGNIN_USER = "sign-in-user";
const SIGNOUT_USER = "sign-out-user";
const AUTH_ERROR = "auth-error";
const CLEAR_AUTH_ERROR = "clear-auth-error";
const SET_GRAPH_READY = "set-graph-ready";
const RESET_AUTH = "reset-auth";
const authReducer = (state, action) => {
  switch (action.type) {
    case SIGNIN_USER:
      return {
        ...state,
        authenticated: true,
        errorMessage: ""
      };
    case SIGNOUT_USER:
      return {
        ...state,
        authenticated: false,
        errorMessage: state.errorMessage || action.payload,
        graphClientReady: false
      };
    case AUTH_ERROR:
      return {
        ...state,
        authenticated: false,
        errorMessage: action.payload
      };
    case CLEAR_AUTH_ERROR:
      return {
        ...state,
        errorMessage: ""
      };
    case SET_GRAPH_READY:
      return {
        ...state,
        graphClientReady: true
      };
    case RESET_AUTH:
      return INITIAL_AUTH_STATE;
    default:
      throw new Error(`Unsupported message resource action type ${action.tyoe}`);
  }
};

/** convenience wrapper around the AuthenticationReduxer in Redux */
export const useAuthentication = (disableWebSockets, appAccessPolicy) => {
  const [auth, dispatch] = React.useReducer(authReducer, INITIAL_AUTH_STATE);
  const [areWebSocketsEnabled, setAreWebSocketsEnabled] = React.useState(!disableWebSockets);
  const {
    addSuccessNotification,
    addFailureNotification,
    clearNotification
  } = useNotificationContext();
  const {
    profile,
    fetchProfile,
    clearProfile,
    hasProfileLoadFailed
  } = useExpertProfileContext();
  const [isSignInActive, setIsSignInActive] = React.useState(true);
  const isAuthenticated = React.useMemo(() => auth.authenticated && auth.graphClientReady, [auth.authenticated, auth.graphClientReady]);
  React.useEffect(() => {
    if (!auth.authenticated) {
      clearNotification();
      if (profile.userName) {
        clearProfile();
      }
    }
  }, [auth]);
  React.useEffect(() => {
    //if we're authenticated and don't have a profile, get one
    if (auth.authenticated && !profile.userName) {
      fetchProfile();
    }
  }, [auth.authenticated, profile.userName]);
  React.useEffect(() => {
    //if the user failed to load they'll be in limbo, sign them out.
    //we'll keep the payload empty as this really is an edge case
    if (hasProfileLoadFailed) {
      dispatch({
        type: SIGNOUT_USER,
        payload: ""
      });
    }
  }, [hasProfileLoadFailed]);
  const toggleWebSockets = () => {
    setAreWebSocketsEnabled(!areWebSocketsEnabled);
    if (areWebSocketsEnabled) {
      addFailureNotification("Web Sockets are now Disabled");
    } else {
      addSuccessNotification("Web Sockets are now Enabled");
    }
  };
  const signOut = (signOutMessage = "") => {
    dispatch({
      type: SIGNOUT_USER,
      payload: signOutMessage
    });
    signOutUser(profile.userName);
  };
  const updateGraphReady = () => {
    dispatch({
      type: SET_GRAPH_READY
    });
  };
  const clearAuthError = () => {
    dispatch({
      type: CLEAR_AUTH_ERROR
    });
  };
  const updateAuthError = errorMessage => {
    dispatch({
      type: AUTH_ERROR,
      payload: errorMessage
    });
  };
  const setUserAsAuthenticated = () => {
    dispatch({
      type: SIGNIN_USER
    });
  };
  const resetAuth = () => {
    dispatch({
      type: RESET_AUTH
    });
  };
  return {
    auth,
    isAuthenticated,
    areWebSocketsEnabled,
    toggleWebSockets,
    signOut,
    updateGraphReady,
    isSignInActive,
    setIsSignInActive,
    updateAuthError,
    clearAuthError,
    setUserAsAuthenticated,
    resetAuth,
    appAccessPolicy
  };
};